import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { RestoreDatabaseDetails } from "../../gen/clients/mchub-azure-api-client-exa";
import { parseId } from "../../helpers/idHelper";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
  EXADB_CDB_UPDATE_POLL_DELAY,
  EXADB_CDB_UPDATE_POLL_INTERVAL,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationConfirmationProps } from "../OperationTypes";

export interface ExaDbRestoreFromBackupProps extends OperationConfirmationProps {
  backup:{
    id: string;
    timeEnded: Date | undefined;
  };
  databaseId: string;
}

const ExaDbRestoreFromBackup = (
  { location, backup, databaseId, onExecute }: ExaDbRestoreFromBackupProps,
): JSX.Element => {
  const { invokeCall: invokeRestore } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).exaDatabaseApi.restoreDatabase },
  );

  React.useEffect(() => {
    const { subscriptionId, resourceGroup, resourceName } = parseId(databaseId);
    const resGroup = decodeURIComponent(resourceGroup || "");
    const databaseName = decodeURIComponent(resourceName || "");

    const { resourceName: backupName } = parseId(backup.id);

    const invokeOptions = {
      onSuccess: onExecute,
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.restore(),
          message: Messages.notifications.inProgress.messages.restoreFromBackup(databaseName, backupName),
        },
        success: {
          title: Messages.notifications.success.titles.restore(),
          message: Messages.notifications.success.messages.restoreFromBackup(databaseName, backupName),
        },
        failure: {
          title: Messages.notifications.failure.titles.restore(),
          message: Messages.notifications.failure.messages.restoreFromBackup(databaseName, backupName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.EXADB_CDB_GET,
          methodArgs: {
            subscriptionId,
            resourceGroupName: resGroup,
            databaseName,
          },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.EXADB_UPDATED_CHECK_CDB,
          delay: EXADB_CDB_UPDATE_POLL_DELAY,
          interval: EXADB_CDB_UPDATE_POLL_INTERVAL,
        },
      },
    };

    invokeRestore({
      subscriptionId,
      resourceGroupName: resGroup,
      databaseName,
      restoreDatabaseDetails:
        { timestamp: backup.timeEnded ? new Date(backup.timeEnded) : undefined } as RestoreDatabaseDetails,
    }, invokeOptions);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div />;
};

export const newExaDbRestoreFromBackup = (
  props: ExaDbRestoreFromBackupProps,
): JSX.Element => (<ExaDbRestoreFromBackup {...props} />);
